import { formatInchesStored } from './casingInches';
import type { MaterialItem, MaterialLineKind, PumpingUnitType, RodMaterialFamily, TransformerCooling } from './procurementRequest';

/** Default patterns; must stay aligned with <c>MaterialSkuDefaults</c> on the API. */
export const DEFAULT_MATERIAL_SKU_PATTERNS: Record<MaterialLineKind, string> = {
  Tubular: 'TBL-{Diameter}-{Weight}-{Grade}-{Thread}-{MfgProcess}',
  StringDesign: 'STR-{Diameter}-{Weight}-{Grade}-{Thread}-{MfgProcess}',
  Tubing: 'TUB-{Diameter}-{Weight}-{Grade}-{Thread}-{MfgProcess}',
  Casing: 'CAS-{Diameter}-{Weight}-{Grade}-{Thread}-{MfgProcess}',
  Rods: 'ROD-{Type}-{Diameter}',
  Transformers: 'TRAN-{VoltageRating}-{Cooling}',
  ElectricMotors: 'ELEC-{Output}-{RPM}-{Volt}',
  PumpingUnits: 'PUMP-{Type}-{LoadRating}',
  LinePipe: 'LP-{Diameter}-{WallThickness}-{EndFinishes}',
  TanksPressureVesselsKnockOuts: 'TPK-{Name}',
  EspCables: 'ESP-{Voltage}-{Temperature}-{Rating}',
  FlaresCombustors: 'FC-{Name}',
  StorageTank: 'STG-{Capacity}-{Pressure}',
  PressureVessel: 'PRVSL-{PressureRating}',
  KnockOut: 'KO-{Type}',
  Flares: 'FLA-{DestructionEfficiency}-{NHV}',
  Combustor: 'COMB-{Performance}'
};

function sanitizeSeg(raw: string | number | undefined | null): string {
  if (raw === undefined || raw === null) {
    return 'X';
  }
  const s = String(raw).trim();
  if (!s) {
    return 'X';
  }
  const t = s
    .replace(/\s+/g, '-')
    .replace(/[^A-Za-z0-9._\-/]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  const u = t.slice(0, 48);
  return u || 'X';
}

function mfgLabel(m: '' | 'Erw' | 'Seamless'): string {
  if (m === 'Erw') {
    return 'ERW';
  }
  if (m === 'Seamless') {
    return 'SMLS';
  }
  return 'X';
}

function rodFamilyLabel(f: RodMaterialFamily): string {
  return f === 'Fiberglass' ? 'FRP' : 'STL';
}

function pumpTypeLabel(t: PumpingUnitType): string {
  return t === 'HydraulicPumping' ? 'HPU' : 'BEAM';
}

function coolingLabel(c?: TransformerCooling): string {
  if (c === 'ONAF') {
    return 'ONAF';
  }
  if (c === 'ONAN') {
    return 'ONAN';
  }
  return 'X';
}

function buildPlaceholders(m: MaterialItem): Record<string, string> {
  const c = m.casing;
  const dCasing = c
    ? formatInchesStored(c.sizeInchesWhole, c.sizeInchesFraction).trim() || c.sizeDiameter?.trim() || ''
    : '';
  const base: Record<string, string> = {
    Diameter: sanitizeSeg(dCasing),
    Weight: c ? sanitizeSeg(c.weight) : 'X',
    Grade: c ? sanitizeSeg(c.grade) : 'X',
    Thread: c ? sanitizeSeg(c.thread) : 'X',
    MfgProcess: c ? mfgLabel(c.mfgProcess) : 'X',
    Name: sanitizeSeg(m.name),
    Output: m.electricMotor?.powerOutputHp != null ? sanitizeSeg(m.electricMotor.powerOutputHp) : 'X',
    RPM: m.electricMotor?.speedRpm != null ? sanitizeSeg(m.electricMotor.speedRpm) : 'X',
    Volt: m.electricMotor?.voltage != null ? sanitizeSeg(m.electricMotor.voltage) : 'X',
    VoltageRating: m.transformer ? sanitizeSeg(m.transformer.voltageRating) : 'X',
    Cooling: m.transformer ? coolingLabel(m.transformer.cooling) : 'X',
    LoadRating: 'X',
    Type: 'X',
    WallThickness: m.linePipe ? sanitizeSeg(m.linePipe.wallThickness) : 'X',
    EndFinishes: m.linePipe ? sanitizeSeg(m.linePipe.endFinishes) : 'X',
    Capacity: m.storageTank?.capacityBbl != null ? sanitizeSeg(m.storageTank.capacityBbl) : 'X',
    Pressure: m.storageTank?.operatingPressurePsig != null ? sanitizeSeg(m.storageTank.operatingPressurePsig) : 'X',
    PressureRating: m.pressureVessel?.pressureRatingPsig != null ? sanitizeSeg(m.pressureVessel.pressureRatingPsig) : 'X',
    DestructionEfficiency:
      m.flare?.destructionEfficiencyPercent != null ? sanitizeSeg(m.flare.destructionEfficiencyPercent) : 'X',
    NHV: m.flare?.netHeatingValueBtuScf != null ? sanitizeSeg(m.flare.netHeatingValueBtuScf) : 'X',
    Performance: m.combustor?.performancePercent != null ? sanitizeSeg(m.combustor.performancePercent) : 'X',
    Voltage: m.espCable?.voltageKv != null ? sanitizeSeg(m.espCable.voltageKv) : 'X',
    Temperature: m.espCable?.temperatureFahrenheit != null ? sanitizeSeg(m.espCable.temperatureFahrenheit) : 'X',
    Rating: m.espCable
      ? sanitizeSeg(
          m.espCable.conductorSizes?.trim() ||
            m.espCable.conductors?.trim() ||
            m.espCable.insulation?.trim() ||
            m.espCable.armor
        )
      : 'X'
  };

  if (m.kind === 'Rods' && m.rods) {
    const r = m.rods;
    const diam = formatInchesStored(r.rodDiameterInchesWhole, r.rodDiameterInchesFraction);
    return {
      ...base,
      Type: rodFamilyLabel(r.materialFamily),
      Diameter: sanitizeSeg(diam || 'X')
    };
  }

  if (m.kind === 'PumpingUnits' && m.pumpingUnit) {
    const p = m.pumpingUnit;
    let lr = 'X';
    if (p.unitType === 'BeamPumping' && p.beam?.loadRatingLbs != null) {
      lr = sanitizeSeg(p.beam.loadRatingLbs);
    } else if (p.unitType === 'HydraulicPumping' && p.hydraulic?.peakLoadLbm != null) {
      lr = sanitizeSeg(p.hydraulic.peakLoadLbm);
    }
    return {
      ...base,
      Type: pumpTypeLabel(p.unitType),
      LoadRating: lr
    };
  }

  if (m.kind === 'LinePipe' && m.linePipe) {
    return {
      ...base,
      Diameter: sanitizeSeg(m.linePipe.diameter)
    };
  }

  if (m.kind === 'KnockOut' && m.knockOut) {
    return {
      ...base,
      Type: m.knockOut.drumType === 'GasKnockOutDrum' ? 'GAS' : m.knockOut.drumType === 'FreeWaterKnockOut' ? 'FW' : 'X'
    };
  }

  return { ...base };
}

export function mergeMaterialSkuPatterns(
  api: Readonly<Record<string, string>> | null | undefined
): Record<MaterialLineKind, string> {
  const o = { ...DEFAULT_MATERIAL_SKU_PATTERNS };
  if (!api) {
    return o;
  }
  for (const [k, v] of Object.entries(api)) {
    const ks = k.trim();
    if (ks in o && v != null && String(v).trim() !== '') {
      (o as Record<string, string>)[ks] = String(v).trim();
    }
  }
  return o;
}

/** Fills {Placeholders} from material data; unknown tokens are left as "X" after replacement pass. */
export function formatMaterialSku(m: MaterialItem, pattern: string | undefined | null): string {
  if (!pattern?.trim()) {
    return '';
  }
  const ph = buildPlaceholders(m);
  const map = ph as Record<string, string>;
  return pattern.replace(/\{([^}]+)\}/g, (_, raw: string) => {
    const key = String(raw).trim();
    const v = map[key];
    return v !== undefined && v !== '' ? v : 'X';
  });
}

export function buildMaterialSku(
  m: MaterialItem,
  patterns: Record<MaterialLineKind, string> | null | undefined
): string {
  if (!patterns) {
    return '';
  }
  const p = patterns[m.kind] ?? DEFAULT_MATERIAL_SKU_PATTERNS[m.kind];
  return formatMaterialSku(m, p);
}
